import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  Inject,
  Input,
  HostListener,
} from "@angular/core";
import { LocationType } from "../models/location-type";
import { StandardService } from "../services/standard.service";
import { Category } from "../models/category";
import { FilteringGuide } from "../models/filteringGuide";
import { WebStorageService, LOCAL_STORAGE } from "ngx-webstorage-service";
import { CryptographyService } from "../services/cryptography.service";
import { FilteringEnum } from "../core/constant";
import {
  sortWithSelectedFirst,
  shouldShowSelectedDivider,
} from "../core/filter-utils";

@Component({
  selector: "app-locations-filter",
  templateUrl: "./locations-filter.component.html",
  styleUrls: ["./locations-filter.component.scss"],
})
export class LocationsFilterComponent implements OnInit {
  locationTypes: LocationType[] = [];
  categories: Category[] = [];
  filterList: FilteringGuide[] = [];
  filteringForCheckBox: FilteringGuide[] = [];

  isLocationDropdownOpen = false;
  locationSearchText = "";
  filteredLocations: LocationType[] = [];

  isCategoryDropdownOpen = false;
  categorySearchText = "";
  filteredCategories: Category[] = [];

  @Output() messageEvent = new EventEmitter<Array<FilteringGuide>>();
  @Input() isNrbes: boolean;

  constructor(
    private standardService: StandardService,
    @Inject(LOCAL_STORAGE)
    public storage: WebStorageService,
    private cryptographyService: CryptographyService
  ) {}

  ngOnInit() {
    this.getMasterDataForFilteration();
  }

  getMasterDataForFilteration() {
    this.standardService
      .GetMasterDataForAddNrbes(
        this.cryptographyService.decryptData(this.storage.get("userRole")),
        this.cryptographyService.decryptData(this.storage.get("userId"))
      )
      .subscribe((data: any) => {
        this.categories = data.categories;
        this.locationTypes = data.locationTypes;
        this.filteredCategories = [...this.categories];
      });
  }

  addFilter($event, id, filterParam) {
    if (this.getFilter(this.isNrbes) != null) {
      this.filterList = this.getFilter(this.isNrbes);
    }
    if ($event.checked) {
      let filterGuide = this.filterList.find((i) => i.type === filterParam);
      if (filterGuide != undefined) {
        if (!filterGuide.ids.includes(id)) {
          filterGuide.ids.push(id);
        }
      } else {
        let guide = new FilteringGuide();
        guide.type = filterParam;
        guide.ids.push(id);
        this.filterList.push(guide);
      }
    } else {
      let selectedFilter = this.filterList.find((i) => i.type === filterParam);
      if (selectedFilter != undefined && selectedFilter.ids.includes(id)) {
        let index = selectedFilter.ids.indexOf(id);
        selectedFilter.ids.splice(index, 1);
      }
      if (selectedFilter.ids.length == 0) {
        let i = this.filterList.findIndex((x) => x.type === filterParam);
        this.filterList.splice(i, 1);
      }
    }
    this.setFilter(this.isNrbes, this.filterList);
    if (
      filterParam === FilteringEnum.FilterByLocationType &&
      this.isLocationDropdownOpen
    ) {
      this.filterLocations();
    }
  }

  applyFilters() {
    this.isLocationDropdownOpen = false;
    this.isCategoryDropdownOpen = false;
    this.messageEvent.emit(this.filterList);
  }

  toggleLocationDropdown() {
    this.isLocationDropdownOpen = !this.isLocationDropdownOpen;
    if (this.isLocationDropdownOpen) {
      this.locationSearchText = "";
      this.setFilteredLocations([...this.locationTypes]);
    }
  }

  filterLocations() {
    const searchTerm = this.locationSearchText.toLowerCase();
    this.setFilteredLocations(
      this.locationTypes.filter((location) =>
        location.name.toLowerCase().includes(searchTerm)
      )
    );
  }

  private setFilteredLocations(items: LocationType[]): void {
    this.filteredLocations = sortWithSelectedFirst(items, (id) =>
      !!this.setCheckedStateForCheckBoxes(FilteringEnum.FilterByLocationType, id)
    );
  }

  toggleCategoryDropdown() {
    this.isCategoryDropdownOpen = !this.isCategoryDropdownOpen;
    if (this.isCategoryDropdownOpen) {
      this.categorySearchText = "";
      this.setFilteredCategories([...this.categories]);
    }
  }

  filterCategories() {
    const searchTerm = this.categorySearchText.toLowerCase();
    this.setFilteredCategories(
      this.categories.filter((category) =>
        category.name.toLowerCase().includes(searchTerm)
      )
    );
  }

  private setFilteredCategories(items: Category[]): void {
    this.filteredCategories = sortWithSelectedFirst(items, (id) =>
      !!this.setCheckedStateForCheckBoxes(FilteringEnum.FilterByCategory, id)
    );
  }

  private getFilterKindConfig(kind: "location" | "category"): {
    items: { id: number; name: string }[];
    filterType: number;
  } {
    switch (kind) {
      case "category":
        return { items: this.categories, filterType: FilteringEnum.FilterByCategory };
      case "location":
      default:
        return { items: this.locationTypes, filterType: FilteringEnum.FilterByLocationType };
    }
  }

  getSelectedItemsCount(kind: "location" | "category"): string[] {
    const { items, filterType } = this.getFilterKindConfig(kind);
    return items
      .filter((item) => this.setCheckedStateForCheckBoxes(filterType, item.id))
      .map((item) => item.name);
  }

  getDisplayText(kind: "location" | "category"): string {
    const selected = this.getSelectedItemsCount(kind);
    if (selected.length === 0) return "";
    if (selected.length === 1) return `: ${selected[0]}`;
    return ` : ${selected[0]} +${selected.length - 1}`;
  }

  showFilterClearButton(kind: "location" | "category"): boolean {
    return this.getSelectedItemsCount(kind).length > 0;
  }

  removeSelected(kind: "location" | "category", event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();

    if (this.getFilter(this.isNrbes) != null) {
      this.filterList = this.getFilter(this.isNrbes);
    }

    if (kind === "location") {
      this.isLocationDropdownOpen = false;
      this.locationSearchText = "";
      this.setFilteredLocations([...this.locationTypes]);
    } else {
      this.isCategoryDropdownOpen = false;
      this.categorySearchText = "";
      this.setFilteredCategories([...this.categories]);
    }

    const { filterType } = this.getFilterKindConfig(kind);
    const filterGuide = this.filterList.find((i) => i.type === filterType);
    if (filterGuide) {
      const index = this.filterList.indexOf(filterGuide);
      if (index !== -1) {
        this.filterList.splice(index, 1);
      }
    }

    this.setFilter(this.isNrbes, this.filterList);
    this.messageEvent.emit(this.filterList);
  }

  showFilterDivider(
    items: { id: number }[],
    index: number,
    filterType: number
  ): boolean {
    return shouldShowSelectedDivider(items, index, (id) =>
      !!this.setCheckedStateForCheckBoxes(filterType, id)
    );
  }

  @HostListener("document:click", ["$event"])
  onClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest(".location-filter-wrapper")) {
      this.isLocationDropdownOpen = false;
    }
    if (!target.closest(".category-filter-wrapper")) {
      this.isCategoryDropdownOpen = false;
    }
  }

  setCheckedStateForCheckBoxes(type, id) {
    this.filteringForCheckBox = this.getFilter(this.isNrbes);
    if (this.filteringForCheckBox != null) {
      let t = this.filteringForCheckBox.find((i) => i.type === type);
      if (t != undefined && t.ids.includes(id)) {
        return true;
      }
    }
  }

  getFilter(isNrbes: boolean) {
    switch (isNrbes) {
      case true: {
        return this.standardService.nrbesFiltrations.filterSet;
      }
      case false: {
        return this.standardService.allStandardsFiltration.filterSet;
      }
      default: {
        break;
      }
    }
  }

  setFilter(isNrbes: boolean, filterList: FilteringGuide[]) {
    switch (isNrbes) {
      case true: {
        return (this.standardService.nrbesFiltrations.filterSet = filterList);
      }
      case false: {
        return (this.standardService.allStandardsFiltration.filterSet =
          filterList);
      }
      default: {
        break;
      }
    }
  }
}
